import {
    doc,
    Firestore, getFirestore,
    writeBatch,
    runTransaction,
    DocumentSnapshot,
    onSnapshot,
    Unsubscribe
} from "firebase/firestore";
import { buildGeohashQuery, TILES_QUERY_NO_LIMIT } from '../helpers/buildGeohashQuery';

/** Max subscription radius for individual tiles — cell aggregation handles zoomed-out views. */
const MAX_TILE_RADIUS_M = 2000;
import {geohashForLocation, geohashQueryBounds,} from 'geofire-common';
import firebaseBackend from "../../../../backend/firebase";
import {setLoading, setTiles, Tile} from "./slice";
import {Color} from "../../color/color";
import {LocationCoordinate} from "../../location/slice";
import { COLLECTIONS } from '@painttheworld/shared';
// Lazy store access to break circular dependency:
// TilesProvider → store → tiles/slice → TilesProvider
const getStore = (): any => require('../../../store').store;
import {addAnonymousTileIds, triggerUpgradePrompt} from "../../auth/slice";
import {UPGRADE_DISMISSED_KEY} from "../../../../components/auth/constants";

// Session-level tile counter for analytics (resets on page reload)
let _sessionTileCount = 0;
export function getSessionTileCount(): number {
    return _sessionTileCount;
}

export default class TilesProvider {
    private static _instance: TilesProvider;
    public static get instance(): TilesProvider {
        if (!TilesProvider._instance) {
            TilesProvider._instance = new TilesProvider();
        }

        return TilesProvider._instance;
    }

    private constructor() {
    }

    _firestore?: Firestore;
    get firestore(): Firestore {
        if (!this._firestore) {
            this._firestore = getFirestore(firebaseBackend);
        }
        return this._firestore;
    }

    static collectionName = COLLECTIONS.TILES;

    private getTileFromDocSnapshot(doc: DocumentSnapshot): Tile {
        const data = doc.data()!;
        return {
            bounds: data.bounds,
            color: data.color,
            coordinate: data.coordinate,
            userId: data.userId,
            glow: data.glow === true,
            sparkle: data.sparkle === true,
        };
    }

    async paintTiles(tiles: Tile[], uid: string | null) {
        const userId = uid ?? 'anonymous';

        const batch = writeBatch(this.firestore);
        const docIds: string[] = [];

        for (const tile of tiles) {
            const hash = geohashForLocation([tile.coordinate.latitude, tile.coordinate.longitude]);
            const docId = `${tile.coordinate.longitude}:${tile.coordinate.latitude}`;
            const docRef = doc(this.firestore, TilesProvider.collectionName, docId);
            batch.set(docRef, {
                ...tile,
                geohash: hash,
                userId,
            }, {
                merge: true
            });
            docIds.push(docId);
        }

        await batch.commit();

        // Increment session tile counter for analytics
        _sessionTileCount += tiles.length;

        // Track anonymous tile IDs so we can upgrade them on sign-in
        if (!uid) {
            getStore().dispatch(addAnonymousTileIds(docIds));
        }
    }

    // Upgrade tiles previously painted as anonymous to the real UID.
    // Only upgrades tiles whose userId is still 'anonymous' — tiles already
    // taken by another player are silently skipped so we never overwrite a
    // legitimate capture. Deleted tiles are also skipped.
    async upgradeTiles(tileIds: string[], newUid: string, newColor?: Color) {
        for (const tileId of tileIds) {
            try {
                const docRef = doc(this.firestore, TilesProvider.collectionName, tileId);
                await runTransaction(this.firestore, async (tx) => {
                    const snap = await tx.get(docRef);
                    if (!snap.exists()) return; // Tile was deleted — skip
                    if (snap.data().userId !== 'anonymous') return; // Taken by another player — skip
                    tx.update(docRef, {
                        userId: newUid,
                        ...(newColor ? { color: newColor } : {}),
                    });
                });
            } catch (err) {
                throw err;
            }
        }
    }

    prevTilesSubscriptions: Unsubscribe[] = [];
    private _expansionTimer?: ReturnType<typeof setTimeout>;

    // Progressive loading: small inner circle delivers fast (~2 s), then mid ring,
    // then full radius fills in the background.  Each phase adds subscriptions on
    // top of the previous ones (old subs stay for real-time updates until disconnect).
    //
    // Measured tile counts for Toronto downtown:
    //   400 m →   451 tiles  (~2 s)
    //   800 m → 1 153 tiles  (~3 s)
    //  full   → 25 201 tiles (~50 s, background)
    static PHASE1_RADIUS_M = 400;
    static PHASE2_RADIUS_M = 800;

    async getTiles(center: LocationCoordinate, radiusInMeters: number) {
        // Cap radius — cell aggregation (TerritoryLayer) handles zoomed-out views
        const cappedRadius = Math.min(radiusInMeters, MAX_TILE_RADIUS_M);
        radiusInMeters = cappedRadius;

        // Cancel any pending phase expansions from a previous call
        if (this._expansionTimer) {
            clearTimeout(this._expansionTimer);
            this._expansionTimer = undefined;
        }

        getStore().dispatch(setLoading(true));

        // Hold old subscriptions until the first inner band delivers, so existing
        // tiles stay visible while the new query warms up.
        const oldSubs = [...this.prevTilesSubscriptions];
        this.prevTilesSubscriptions = [];
        let oldCancelled = false;
        const cancelOld = () => {
            if (!oldCancelled) {
                oldCancelled = true;
                for (const u of oldSubs) u();
            }
        };
        // Safety net: always cancel old subs after 10 s even if new ones stall
        const safetyTimer = setTimeout(cancelOld, 10000);
        this.prevTilesSubscriptions.push(() => clearTimeout(safetyTimer));

        const phase1Radius = Math.min(TilesProvider.PHASE1_RADIUS_M, radiusInMeters);
        const phase1Bounds = geohashQueryBounds([center.latitude, center.longitude], phase1Radius);
        let phase1Delivered = 0;

        if (phase1Bounds.length === 0) {
            getStore().dispatch(setLoading(false));
            return;
        }

        for (const b of phase1Bounds) {
            this.prevTilesSubscriptions.push(this.subscribe(b, (snapshot) => {
                cancelOld();
                this.handleSnapshot(snapshot);
                phase1Delivered++;
                if (phase1Delivered === phase1Bounds.length) {
                    // Only mark done if no further phases will run
                    if (!this.scheduleExpansion(center, radiusInMeters, /* phase */ 2)) {
                        getStore().dispatch(setLoading(false));
                    }
                }
            }));
        }
    }

    // Phase radii in ascending order; fullRadius is appended as the final stage.
    private phaseRadius(phase: number, fullRadius: number): number {
        const steps = [TilesProvider.PHASE1_RADIUS_M, TilesProvider.PHASE2_RADIUS_M];
        return Math.min(steps[phase - 1] ?? fullRadius, fullRadius);
    }

    // Returns true if a new phase was scheduled, false if this was the terminal phase.
    private scheduleExpansion(center: LocationCoordinate, fullRadius: number, phase: number): boolean {
        const nextRadius = this.phaseRadius(phase, fullRadius);
        const prevRadius = this.phaseRadius(phase - 1, fullRadius);
        // Skip if this phase adds no new area (e.g. fullRadius <= PHASE1_RADIUS_M)
        if (nextRadius <= prevRadius) return false;

        this._expansionTimer = setTimeout(() => {
            this._expansionTimer = undefined;

            const bounds = geohashQueryBounds([center.latitude, center.longitude], nextRadius);
            let delivered = 0;
            for (const b of bounds) {
                this.prevTilesSubscriptions.push(this.subscribe(b, (snapshot) => {
                    this.handleSnapshot(snapshot);
                    delivered++;
                    if (delivered === bounds.length) {
                        // Schedule next phase; if none, all phases are done
                        if (!this.scheduleExpansion(center, fullRadius, phase + 1)) {
                            getStore().dispatch(setLoading(false));
                        }
                    }
                }));
            }
        }, 100);
        return true;
    }

    private subscribe(band: [string, string], onData: (snapshot: any) => void): Unsubscribe {
        const q = buildGeohashQuery(this.firestore, TilesProvider.collectionName, band, TILES_QUERY_NO_LIMIT);
        return onSnapshot(q, onData, (error) => {
            console.error('[TilesProvider] snapshot error:', error);
        });
    }

    private handleSnapshot(snapshot: any) {
        const changedTiles: Tile[] = [];
        let shouldPromptUpgrade = false;

        // Build Set once per snapshot for O(1) lookup instead of O(n) per tile
        const anonymousTileSet = new Set(getStore().getState().auth.anonymousTileIds);

        snapshot.docChanges().forEach((change: any) => {
            switch (change.type) {
                case "added":
                case "modified":
                    changedTiles.push(this.getTileFromDocSnapshot(change.doc));
                    if (change.type === 'modified') {
                        const newUserId = change.doc.data().userId;
                        const currentUid = getStore().getState().auth.uid;
                        if (
                            newUserId &&
                            newUserId !== 'anonymous' &&
                            newUserId !== currentUid &&
                            anonymousTileSet.has(change.doc.id) &&
                            !sessionStorage.getItem(UPGRADE_DISMISSED_KEY)
                        ) {
                            shouldPromptUpgrade = true;
                        }
                    }
                    break;
                case "removed":
                    break;
            }
        });

        if (changedTiles.length > 0) {
            getStore().dispatch(setTiles(changedTiles));
        }
        if (shouldPromptUpgrade) {
            getStore().dispatch(triggerUpgradePrompt());
        }
    }

    disconnect() {
        if (this._expansionTimer) {
            clearTimeout(this._expansionTimer);
            this._expansionTimer = undefined;
        }
        for (const unsub of this.prevTilesSubscriptions) {
            unsub();
        }
        this.prevTilesSubscriptions = [];
        getStore().dispatch(setLoading(false));
    }
}
